import type { Config } from "../../../../aysa_api/models/config.types";
import type { TargetGroup } from "../../../../aysa_api/models/target_group.types";
import type {
  OptionalCustomerSession,
  Session,
} from "../../../../aysa_api/models/session.types";
import type {
  SponsoredContentSection,
  StaticCategoriesSection,
  KidsStaticCategoriesSection,
  CardSliderSection,
} from "../../../../aysa_api/models/sections.types";
import type {
  SearchTracker,
  PromotionTracker,
} from "../../../../aysa_api/models/tracker.types";
import type { Advertisement } from "../../../../aysa_api/models/advertisement.types";
import type { ReductionsState } from "../../../../aysa_api/models/reductions_state.types";
import type { Image } from "../../../../aysa_api/models/image.types";
import type { Brand } from "../../../../aysa_api/models/brand.types";
import type { Category } from "../../../../aysa_api/models/category.types";
import type { ProductTile } from "../../../../aysa_api/models/product_tile.types";
import type { ProductTileV2 } from "../../../../aysa_api/models/product_tile_v2.types";
import type { Pagination } from "../../../../aysa_api/models/pagination.types";
import type {
  AppliedFilters,
  FilterSection,
} from "../../../../aysa_api/models/filter_section.types";
import type { ProductImageTypes } from "../../../../aysa_api/models/product_images.types";
import type { CategoryIdentifier } from "../../../../aysa_api/models/category_identifier.types";
import type { Page } from "../../../../aysa_api/models/page.types";
import type { StreamItem } from "../../../../aysa_api/services/search/v1/stream.types";
import type { TreeNavigationItem } from "../../../../aysa_api/models/tree_navigation_item.types";
import type {
  NavigationSection,
  NavigationSection_Category,
} from "../../../../aysa_api/models/navigation_section.types";

export enum SearchSuggestionTypes {
  UNSPECIFIED = 0,
  TERMS = 1,
  BRANDS = 2,
}

export interface GetEmptySearchPageRequest {
  config: Config;
  targetGroup: TargetGroup;
  session?: OptionalCustomerSession;
}

export interface GetEmptySearchPageResponse {
  section: GetEmptySearchPageResponse_Section;
}

export interface GetEmptySearchPageResponse_Section {
  type?:
    | {
        $case: "staticCategories";
        staticCategories: StaticCategoriesSection;
      }
    | {
        $case: "kidsCategories";
        kidsCategories: KidsStaticCategoriesSection;
      }
    | {
        $case: "cardSlider";
        // can be either the popular brands slider or popular categories slider
        cardSlider: CardSliderSection;
      }
    | {
        $case: "sponsoredSlider";
        sponsoredSlider: GetEmptySearchPageResponse_SponsoredSliderSection;
      };
}

export interface GetEmptySearchPageResponse_SponsoredSliderSection {
  slide: SponsoredContentSection[];
}

export interface GetResolvedTermRequest {
  config: Config;
  targetGroup: TargetGroup;
  term: string;
  session?: Session;
}

export interface GetResolvedTermResponse {
  searchTracker: SearchTracker;
  result?:
    | {
        $case: "noResult";
        noResult: GetResolvedTermResponse_NoResult;
      }
    | {
        $case: "resolvedUrl";
        resolvedUrl: GetResolvedTermResponse_ResolvedURL;
      };
}

export interface GetResolvedTermResponse_NoResult {
  // The sanitized term that could not be resolved
  term: string;
  // JWT which will be stable through all the requests and is provided by BI
  searchToken?: string;
  recommendedTargetGroup?: TargetGroup;
  // Example: "Hey, wir haben leider keine überreste von adidas, aber hier sind ein paar gute alternativen von adidas!"
  message?: string;
}

export interface GetResolvedTermResponse_ResolvedURL {
  // The resolved URL to redirect the user to. This also includes applied filters.
  url: string;
  // HTTP status code to use for the redirect.
  // 301 -> redirects to brand and category pages
  // 302 -> redirects from the URL manager
  statusCode: number;
  // Recommended target group based on the resolved term
  recommendedTargetGroup: TargetGroup;
  // Example: "Hey, wir haben leider keine überreste von adidas, aber hier sind ein paar gute alternativen von adidas!"
  message?: string;
}

export interface GetSuggestionsRequest {
  config: Config;
  // The currently selected target group by the client.
  targetGroup: TargetGroup;
  // The search term the user alredy entered to request suggestions for
  searchTerm: string;
  // Limit the results, defaults to 10, hard limited to 20 on the
  limit?: number;
}

export interface GetSuggestionsResponse {
  suggestions: GetSuggestionsResponse_Suggestion[];
}

export interface GetSuggestionsResponse_Suggestion {
  // The actual suggested term to show the user, the matching part will be marked with <mark></mark>
  term: string;
  type: SearchSuggestionTypes;
  count?: string;
  hitScore?: string;
  // relative url to redirect the user to once a suggestion was selected
  resolveUrl: string;
  // indicates whether the term itself can be resolved or not
  resolvable: boolean;
}

export interface GetBasBannerRequest {
  config: Config;
  session: OptionalCustomerSession;
  // The currently selected target group by the client.
  targetGroup: TargetGroup;
}

export interface GetBasBannerResponse {
  // BAS Banner
  advertisement?: Advertisement;
}

export interface GetEmptySearchRecommendationsRequest {
  config: Config;
  targetGroup: TargetGroup;
  reductionsState: ReductionsState;
}

export interface GetEmptySearchRecommendationsResponse {
  recommendations: GetEmptySearchRecommendationsResponse_EmptySearchRecommendation[];
}

export interface GetEmptySearchRecommendationsResponse_TopSearchedBrands {
  headline: string;
  icon: Image;
  brands: GetEmptySearchRecommendationsResponse_TopSearchedBrands_SuggestedBrand[];
}

export interface GetEmptySearchRecommendationsResponse_TopSearchedBrands_SuggestedBrand {
  brand: Brand;
  tracker: PromotionTracker;
}

export interface GetEmptySearchRecommendationsResponse_PopularCategories {
  headline: string;
  icon: Image;
  categories: GetEmptySearchRecommendationsResponse_PopularCategories_SuggestedCategory[];
}

export interface GetEmptySearchRecommendationsResponse_PopularCategories_SuggestedCategory {
  category: Category;
  tracker: PromotionTracker;
}

export interface GetEmptySearchRecommendationsResponse_TopProducts {
  headline: string;
  icon: Image;
  deprecatedProducts: ProductTile[];
  categoryId: number;
  products: ProductTileV2[];
}

export interface GetEmptySearchRecommendationsResponse_EmptySearchRecommendation {
  item?:
    | {
        $case: "topSearchedBrands";
        topSearchedBrands: GetEmptySearchRecommendationsResponse_TopSearchedBrands;
      }
    | {
        $case: "popularCategories";
        popularCategories: GetEmptySearchRecommendationsResponse_PopularCategories;
      }
    | {
        $case: "topProducts";
        topProducts: GetEmptySearchRecommendationsResponse_TopProducts;
      };
}

export interface GetEmptySearchCategoriesRequest {
  config: Config;
  targetGroup: TargetGroup;
}

export interface GetEmptySearchCategoriesResponse {
  section: GetEmptySearchCategoriesResponse_Section;
}

export interface GetEmptySearchCategoriesResponse_Section {
  type?:
    | {
        $case: "staticCategories";
        staticCategories: StaticCategoriesSection;
      }
    | {
        $case: "kidsCategories";
        kidsCategories: KidsStaticCategoriesSection;
      }
    | {
        $case: "cardSlider";
        cardSlider: CardSliderSection;
      };
}

export interface GetProductSearchResultsRequest {
  config: Config;
  targetGroup: TargetGroup;
  query: string;
  pagination?: Pagination;
}

export interface GetProductSearchResultsResponse {
  products: GetProductSearchResultsResponse_Product[];
}

export interface GetProductSearchResultsResponse_Product {
  productId: number;
  similarityScore: number;
}

export interface GetSearchResultsPageBulkRequest {
  config: Config;
  targetGroup: TargetGroup;
  // as retrieved from resolved term endpoint
  searchToken: string;
  elementCount: number;
  appliedFilters?: AppliedFilters;
  preferredProductImageType?: ProductImageTypes;
  session?: Session;
  reductionsState: ReductionsState;
  category?: CategoryIdentifier;
}

export interface GetSearchResultsPageBulkResponse {
  meta: GetSearchResultsPageBulkResponse_Meta;
  filters: GetSearchResultsPageBulkResponse_Filters;
  stream: GetSearchResultsPageBulkResponse_Stream;
  categoryTree: GetSearchResultsPageBulkResponse_CategoryTree;
  categoryNavigation: GetSearchResultsPageBulkResponse_CategoryNavigation;
  mainCategory: GetSearchResultsPageBulkResponse_MainCategory;
  navigationHeader: GetSearchResultsPageBulkResponse_NavigationHeader;
}

export interface GetSearchResultsPageBulkResponse_Meta {
  meta: Page;
}

export interface GetSearchResultsPageBulkResponse_Stream {
  sections: StreamItem[];
  // unset indicates the end of the stream
  continuationToken?: string;
}

export interface GetSearchResultsPageBulkResponse_Filters {
  // This returns the available filter sections that should be displayed.
  // Empty filters indicate that filters should be hidden.
  filters: FilterSection[];
  // The actually applied filters
  // Applied filters in the response may differ from the request, e.g. due to dropped filters
  appliedFilters: AppliedFilters;
  // e.g Filters
  title: string;
  // Amount of applied filter values
  // e.g. color = red and green & size = M => 3
  appliedFiltersCount: number;
}

export interface GetSearchResultsPageBulkResponse_CategoryTree {
  categoryTree: TreeNavigationItem[];
}

export interface GetSearchResultsPageBulkResponse_CategoryNavigation {
  navigationSection: NavigationSection;
}

export interface GetSearchResultsPageBulkResponse_MainCategory {
  mainCategory: NavigationSection_Category;
}

export interface GetSearchResultsPageBulkResponse_NavigationHeader {
  // The requested category which the user is currently on
  category: Category;
  // The list of categories which should be shown as the sub category navigation
  //
  // In case `category` is a leaf category, the categories will be the children of the parent category of `category`.
  // In all other cases, the `categories` will just be the children of the `category`.
  categories: GetSearchResultsPageBulkResponse_NavigationHeader_CategoryItem[];
  // The direct parent category of `category`.
  //
  // This field will also not be set if the `category` is either a top level category (as these don't have parent)
  // but this will also not be set for second level categories (e.g. Clothes, Shoes etc.).
  parentCategory?: Category;
}

// Class which should be shown as the sub category navigation
export interface GetSearchResultsPageBulkResponse_NavigationHeader_CategoryItem {
  category: Category;
  hasProducts: boolean;
}

export interface GetSearchResultsStreamRequest {
  config: Config;
  targetGroup: TargetGroup;
  // as retrieved from resolved term endpoint
  searchToken: string;
  elementCount: number;
  appliedFilters?: AppliedFilters;
  continuationToken: string;
  preferredProductImageType?: ProductImageTypes;
  session?: Session;
  reductionsState: ReductionsState;
  category?: CategoryIdentifier;
}

export interface GetSearchResultsStreamResponse {
  sections: StreamItem[];
  // unset indicates the end of the stream
  continuationToken?: string;
}
